import {addDays, compareAsc, isValid, subDays} from 'date-fns';
import Decimal from 'decimal.js';
import {
  Button,
  ButtonGroup,
  Choice,
  CurrencyInput,
  Dropdown,
  DropdownItem,
  FormButton,
  FormControl,
  FormField,
  Label,
  NumberInput,
  openConfirmDialog,
  OptionType,
  Separator,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, Space, VStack} from 'platform/foundation';

import {Fragment} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {head, indexBy, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetServiceContractsQuery} from '@dms/api/customer';
import {
  AftersalesPaymentMethod,
  Payment,
  useGetCheckoutPaymentTemplatesQuery,
  usePutCheckoutSetTypeOfSaleMutation,
} from '@dms/api/sales';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {
  DEFAULT_FOREIGN_CURRENCY,
  handleApiError,
  PredefinedNotes,
  useBank,
  useCashRegister,
  useTenant,
} from '@dms/shared';

import {
  buildArray,
  CurrencyCodeType,
  Nullish,
  precisionCalculation,
  RequiredTestIdProps,
  suffixTestId,
  useBoolean,
} from 'shared/web';

import {useFormValueChange} from '../hooks/useFormValueChange';
import {CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';
import {IssueRestrictionResponse} from '../types/IssueRestrictionResponse';

interface CheckoutPaymentFormBodyProps extends RequiredTestIdProps {
  checkoutId: string;
  control: FormControl<CheckoutPaymentFormType>;
  customerId: string;
  formApi: UseFormReturn<CheckoutPaymentFormType>;
  allowedPaymentMethods?: AftersalesPaymentMethod[];
  isSubmitButtonDisabled?: boolean;
  issueRestrictions?: IssueRestrictionResponse[];
  isForeignCurrencyAllowed?: boolean;
  payment: Payment;
  totalAmount?: number;
  onAddAnotherDepositClick?: () => Promise<unknown>;
  onRemoveDepositClick?: () => Promise<unknown>;
  onSaveChanges: (data: CheckoutPaymentFormType) => Promise<unknown> | undefined;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  isPostCheckoutPaymentIssueLoading?: boolean;
}

export function CheckoutPaymentFormBody(props: CheckoutPaymentFormBodyProps) {
  const {control, formApi} = props;

  const serviceContracts = useGetServiceContractsQuery({customerId: props.customerId});
  const {bankAccountOptions, primaryBankAccountOption} = useBank();
  const {cashRegisterOptions} = useCashRegister();
  const {data} = useGetTenantQuery();
  const {tenant} = useTenant();

  const [isRemoveLoading, startRemoveLoading, stopRemoveLoading] = useBoolean();

  const [putCheckoutSetTypeOfSale] = usePutCheckoutSetTypeOfSaleMutation();

  const {data: paymentTemplates} = useGetCheckoutPaymentTemplatesQuery({
    checkoutId: props.checkoutId,
    paymentId: props.payment.checkoutPaymentId,
  });

  const {data: authorizationProfiles} = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenant?.id ?? ''},
    {skip: isNil(tenant)}
  );

  const authProfilesById = indexBy((p) => p.id, authorizationProfiles ?? []);

  const authProfileTemplates = (paymentTemplates ?? []).filter((t) =>
    isNotNil(t.authorizationProfileId)
  );
  const defaultTemplates = (paymentTemplates ?? []).filter((t) => isNil(t.authorizationProfileId));
  const uniqueProfileIds = authProfileTemplates.reduce<string[]>((acc, t) => {
    const id = t.authorizationProfileId!;
    return acc.includes(id) ? acc : [...acc, id];
  }, []);

  const [issueDate, paymentMethod, note, exchangeRate, amount] = useWatch({
    control: formApi.control,
    name: ['issueDate', 'method', 'note', 'exchangeRate', 'amount'],
  });
  const templateId = useWatch({control: formApi.control, name: 'templateId'});

  const validIssueDate = issueDate && isValid(issueDate) ? issueDate : null;
  const minTaxableEventDate = validIssueDate ? subDays(validIssueDate, 15) : new Date();
  const maxTaxableEventDate = validIssueDate ? validIssueDate : new Date();

  const currencyData = data?.currency as CurrencyCodeType | undefined;
  const serviceContractOptions =
    serviceContracts.data?.map((serviceContract) => ({
      label: serviceContract.number,
      value: serviceContract.serviceContractId,
    })) ?? [];

  const percentage = props.totalAmount
    ? parseFloat(
        precisionCalculation
          .multiply(precisionCalculation.divide(amount ?? 0, props.totalAmount ?? 0), 100)
          .toFixed(2)
      )
    : 0;

  useFormValueChange(
    exchangeRate,
    props.payment.exchangeRate ? parseFloat(props.payment.exchangeRate ?? '0') : null,
    (value) => formApi.setValue('exchangeRate', value)
  );

  useFormValueChange(amount, props.payment.paymentAmount.amount, (value) =>
    formApi.setValue('amount', value)
  );

  useFormValueChange(paymentMethod, props.payment.method, (value) =>
    formApi.setValue('method', value)
  );

  const handlePercentageChange = (value: number | null) => {
    const newPercentage = parseFloat(
      precisionCalculation.multiply((value ?? 0) / 100, props.totalAmount ?? 0).toFixed(2)
    );

    formApi.setValue('amount', newPercentage);
    handleSaveChanges();
  };

  const afterPaymentChange = (newValue: AftersalesPaymentMethod) => {
    const issuedOn = formApi.getValues('issueDate');
    const validIssuedOn = issuedOn && isValid(issuedOn) ? issuedOn : null;

    if (newValue === 'BANK_TRANSFER' && isNotNil(validIssuedOn)) {
      const DEFAULT_DUE_DATE = 14;

      formApi.setValue('due', DEFAULT_DUE_DATE, {shouldValidate: true});
      formApi.setValue('dueDate', addDays(validIssuedOn, DEFAULT_DUE_DATE), {shouldValidate: true});

      if (isNilOrEmpty(formApi.getValues('bankAccount'))) {
        formApi.setValue('bankAccount', primaryBankAccountOption?.value ?? '', {
          shouldValidate: true,
        });
      }

      return;
    }

    if (newValue === 'CASH' && isNilOrEmpty(formApi.getValues('cashRegisterId'))) {
      formApi.setValue('cashRegisterId', head(cashRegisterOptions)?.value ?? '', {
        shouldValidate: true,
      });
    }

    if (isNotNil(validIssuedOn)) {
      formApi.setValue('due', 0, {shouldValidate: true});
      formApi.setValue('dueDate', validIssuedOn, {shouldValidate: true});
    }
  };

  const handleSetPaymentMethod = (value: AftersalesPaymentMethod) => {
    formApi.setValue('method', value);
    afterPaymentChange(value);

    handleSaveChanges();
  };

  const handlePaymentMethodChange = (newValue: AftersalesPaymentMethod | Nullish) => {
    if (isNil(newValue)) {
      return;
    }

    if (newValue === 'INTERNAL') {
      openConfirmDialog({
        onConfirm: () =>
          putCheckoutSetTypeOfSale({
            checkoutId: props.checkoutId,
            body: {typeOfSale: 'INTERNAL'},
          })
            .unwrap()
            .then(() => handleSetPaymentMethod(newValue))
            .catch(handleApiError),
        text: i18n.t('entity.checkout.labels.freeOfChargeHelperText'),
      });
      return;
    }

    if (paymentMethod === 'INTERNAL') {
      openConfirmDialog({
        onConfirm: () =>
          putCheckoutSetTypeOfSale({
            checkoutId: props.checkoutId,
            body: {typeOfSale: 'DEFAULT'},
          })
            .unwrap()
            .then(() => handleSetPaymentMethod(newValue))
            .catch(handleApiError),
        text: i18n.t('entity.invoice.notifications.freeOfChargeChangeHelperText'),
      });
      return;
    }

    handleSetPaymentMethod(newValue);
  };

  const handleIssuedOnChange = (issueDate: Date | Nullish) => {
    if (isNil(issueDate) || !isValid(issueDate)) {
      return;
    }

    const due = formApi.getValues('due') ?? 0;
    const calculatedDueDate = addDays(issueDate, due);
    if (isValid(calculatedDueDate)) {
      formApi.setValue('dueDate', calculatedDueDate, {shouldValidate: true});
    }

    const taxableEventDate = formApi.getValues('dateOfTaxableSupply');

    const minTaxableEventDate = subDays(issueDate, 15);
    const maxTaxableEventDate = issueDate;

    if (!taxableEventDate) {
      return formApi.setValue('dateOfTaxableSupply', issueDate, {shouldValidate: true});
    }

    if (compareAsc(taxableEventDate, minTaxableEventDate) < 0) {
      return formApi.setValue('dateOfTaxableSupply', minTaxableEventDate, {shouldValidate: true});
    }

    if (compareAsc(taxableEventDate, maxTaxableEventDate) > 0) {
      return formApi.setValue('dateOfTaxableSupply', maxTaxableEventDate, {shouldValidate: true});
    }
  };

  const handleDueChange = (due: number | Nullish) => {
    if (isNil(due)) {
      return;
    }

    if (due < 0 || due > 3650) {
      if (due < 0) {
        return formApi.setValue('due', 0, {shouldValidate: true});
      } else {
        return formApi.setValue('due', 3650, {shouldValidate: true});
      }
    }

    const issuedOn = formApi.getValues('issueDate');
    const validIssuedOn = issuedOn && isValid(issuedOn) ? issuedOn : null;

    if (isNotNil(validIssuedOn)) {
      const calculatedDueDate = addDays(validIssuedOn, due);
      if (isValid(calculatedDueDate)) {
        formApi.setValue('dueDate', calculatedDueDate, {shouldValidate: true});
      }
    }
  };

  const handleDueDateChange = (date: Date | Nullish) => {
    if (isNil(date) || !isValid(date)) {
      return;
    }

    const due = formApi.getValues('due') ?? 0;
    formApi.setValue('issueDate', subDays(date, due), {shouldValidate: true});
  };

  const handleSaveChanges = () => {
    props.onSaveChanges(formApi.getValues());
  };

  const handleTemplateSelect = (id: string) => {
    formApi.setValue('templateId', id);
    handleSaveChanges();
  };

  const selectedTemplate = (paymentTemplates ?? []).find((t) => t.id === templateId);
  const fallbackTemplate = (paymentTemplates ?? []).find((t) => t.isDefault);
  const templateButtonTitle =
    selectedTemplate?.title ?? fallbackTemplate?.title ?? i18n.t('general.labels.template');

  const handleRemoveDeposit = () => {
    if (!props.onRemoveDepositClick) {
      return;
    }

    startRemoveLoading();
    props.onRemoveDepositClick().finally(stopRemoveLoading);
  };

  const orderPaymentOptions = buildArray<OptionType<AftersalesPaymentMethod>>()
    .when(props.allowedPaymentMethods?.includes('BANK_TRANSFER'), {
      label: i18n.t('entity.invoice.paymentMethod.bankTransfer'),
      value: 'BANK_TRANSFER',
    })
    .when(props.allowedPaymentMethods?.includes('CARD'), {
      label: i18n.t('entity.invoice.paymentMethod.card'),
      value: 'CARD',
    })
    .when(props.allowedPaymentMethods?.includes('CASH'), {
      label: i18n.t('entity.invoice.paymentMethod.cash'),
      value: 'CASH',
    })
    .when(props.allowedPaymentMethods?.includes('INTERNAL'), {
      label: i18n.t('general.labels.internal'),
      value: 'INTERNAL',
    });

  const foreignCurrencyAmount =
    exchangeRate && amount ? parseFloat(new Decimal(amount).div(exchangeRate).toFixed(2)) : 0;

  return (
    <VStack spacing={4}>
      <Grid spacing={4} columns={4}>
        <FormField
          control={control}
          name="amount"
          type="currency"
          currency={currencyData}
          label={i18n.t('general.labels.amount')}
          isDisabled={props.payment.discriminator === 'BALANCE'}
          isRequired
          data-testid={suffixTestId('amount', props)}
        />
        <Show when={props.payment.discriminator === 'DEPOSIT'}>
          <NumberInput
            suffix="%"
            value={percentage}
            onChange={handlePercentageChange}
            isDisabled={props.isEditingDisabled}
            label={i18n.t('entity.order.labels.percentage')}
          />
        </Show>
        <Show when={props.isForeignCurrencyAllowed}>
          <FormField
            control={control}
            name="exchangeRate"
            type="currency"
            currency={currencyData}
            label={i18n.t('general.labels.exchangeRate')}
            isRequired
            data-testid={suffixTestId('exchangeRate', props)}
          />
          <CurrencyInput
            value={foreignCurrencyAmount}
            isDisabled
            label={i18n.t('entity.checkout.labels.amountInForeignCurrency')}
            currency={
              (props.payment.foreignCurrencyPaymentAmount?.currency ??
                DEFAULT_FOREIGN_CURRENCY) as CurrencyCodeType
            }
          />
        </Show>
      </Grid>
      <Grid spacing={4} columns={4}>
        <FormField
          control={control}
          name="issueDate"
          type="date"
          isRequired
          isRelativeDatesHidden
          label={i18n.t('entity.checkout.labels.issuedOn')}
          onChange={handleIssuedOnChange}
          data-testid={suffixTestId('issueDate', props)}
          isNonClearable
        />
        <FormField
          control={control}
          name="due"
          type="number"
          label={i18n.t('entity.checkout.labels.due')}
          onChange={handleDueChange}
          data-testid={suffixTestId('due', props)}
          minStepperValue={0}
          maxStepperValue={36500}
          isStepperVisible
          isRequired
        />
        <FormField
          control={control}
          name="dueDate"
          type="date"
          isRelativeDatesHidden
          label={i18n.t('entity.checkout.labels.dueDate')}
          onChange={handleDueDateChange}
          data-testid={suffixTestId('dueDate', props)}
          isRequired
          isNonClearable
        />
        <FormField
          control={control}
          name="dateOfTaxableSupply"
          type="date"
          isRelativeDatesHidden
          label={i18n.t('entity.checkout.labels.taxableEventDate')}
          data-testid={suffixTestId('taxableEventDate', props)}
          maxDate={maxTaxableEventDate}
          minDate={minTaxableEventDate}
          isRequired
          isNonClearable
        />
        <GridItem span={1}>
          <Choice<AftersalesPaymentMethod>
            name="paymentMethod"
            value={paymentMethod}
            label={i18n.t('entity.checkout.labels.paymentMethod')}
            options={orderPaymentOptions}
            onChange={(value) => handlePaymentMethodChange(value)}
            isNotClearable
            isDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('paymentMethod', props)}
          />
        </GridItem>
        <GridItem span={1}>
          <Show when={paymentMethod === 'BANK_TRANSFER'}>
            <FormField
              control={control}
              name="bankAccount"
              type="choice"
              options={bankAccountOptions}
              label={i18n.t('entity.checkout.labels.incomingBankAccount')}
              isRequired
              isNotClearable
              data-testid={suffixTestId('incomingBankAccount', props)}
            />
          </Show>
          <Show when={paymentMethod === 'CASH'}>
            <FormField
              control={control}
              name="cashRegisterId"
              type="choice"
              options={cashRegisterOptions}
              label={i18n.t('entity.checkout.labels.cashRegister')}
              isNotClearable
              data-testid={suffixTestId('incomingBankAccount', props)}
            />
          </Show>
        </GridItem>
        <GridItem span={1}>
          <FormField
            control={control}
            name="customerServiceContractId"
            type="choice"
            options={serviceContractOptions}
            label={i18n.t('entity.checkout.labels.customerServiceContract')}
            data-testid={suffixTestId('customerServiceContract', props)}
          />
        </GridItem>
      </Grid>
      <VStack spacing={1}>
        <HStack justify="space-between" align="flex-end">
          <Label>{i18n.t('general.labels.note')}</Label>
          <PredefinedNotes
            note={note ?? null}
            onPrefill={(note) => props.formApi.setValue('note', note)}
            resource="SERVICE_CASE"
            context="service_checkout"
            isLinkVariant
            isEditingDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('predefinedNotes', props)}
          />
        </HStack>
        <FormField
          control={control}
          name="note"
          type="textarea"
          data-testid={suffixTestId('note', props)}
        />
      </VStack>
      <HStack>
        {props.onAddAnotherDepositClick && (
          <Button
            variant="link"
            title={i18n.t('entity.checkout.addAnotherPayment')}
            leftIcon="content/add_circle"
            onClick={props.onAddAnotherDepositClick}
            isDisabled={props.isEditingDisabled}
          />
        )}
        <Space fillAvailable />
        <ButtonGroup>
          {props.onRemoveDepositClick && (
            <Button
              variant="dangerLink"
              title={i18n.t('entity.checkout.removePayment')}
              onClick={handleRemoveDeposit}
              isDisabled={isRemoveLoading || props.isEditingDisabled}
              isLoading={isRemoveLoading}
            />
          )}
          <Show when={isNotNilOrEmpty(paymentTemplates)}>
            <Dropdown
              isHeightLimited
              isDisabled={
                props.isEditingDisabled ||
                props.isCompletingDisabled ||
                props.isPostCheckoutPaymentIssueLoading
              }
              dropdownControl={
                <Button
                  variant="link"
                  rightIcon="navigation/expand_more"
                  title={templateButtonTitle}
                  isDisabled={
                    props.isEditingDisabled ||
                    props.isCompletingDisabled ||
                    props.isPostCheckoutPaymentIssueLoading
                  }
                  isLoading={props.isPostCheckoutPaymentIssueLoading}
                  data-testid={suffixTestId('templateDropdown', props)}
                />
              }
              data-testid={suffixTestId('templateDropdown-menu', props)}
            >
              {uniqueProfileIds.map((profileId, profileIndex) => {
                const profileTemplates = authProfileTemplates.filter(
                  (t) => t.authorizationProfileId === profileId
                );
                const profileName = authProfilesById[profileId]?.name;
                return (
                  <Fragment key={profileId}>
                    {profileIndex > 0 && <Space vertical={2} />}
                    <Label>{profileName ?? i18n.t('general.labels.template')}</Label>
                    {profileTemplates.map((template, index) => (
                      <DropdownItem
                        key={template.id}
                        label={template.title}
                        onClick={() => handleTemplateSelect(template.id)}
                        data-testid={suffixTestId(
                          `authProfileTemplate[${profileIndex}][${index}]`,
                          props
                        )}
                      />
                    ))}
                    {(profileIndex < uniqueProfileIds.length - 1 ||
                      isNotNilOrEmpty(defaultTemplates)) && <Separator spacing={0} />}
                  </Fragment>
                );
              })}
              <Show when={isNotNilOrEmpty(defaultTemplates)}>
                <Show when={isNotNilOrEmpty(uniqueProfileIds)}>
                  <Space vertical={2} />
                  <Label>
                    {i18n.t('entity.authorizationProfiles.labels.withoutAuthorization')}
                  </Label>
                </Show>
                {defaultTemplates.map((template, index) => (
                  <DropdownItem
                    key={template.id}
                    label={template.title}
                    onClick={() => handleTemplateSelect(template.id)}
                    data-testid={suffixTestId(`defaultTemplate[${index}]`, props)}
                  />
                ))}
              </Show>
            </Dropdown>
          </Show>
          <FormButton
            isLoading={props.isPostCheckoutPaymentIssueLoading}
            control={control}
            type="submit"
            isDisabled={
              props.isSubmitButtonDisabled ||
              isNotNilOrEmpty(props.issueRestrictions) ||
              props.isCompletingDisabled ||
              props.isPostCheckoutPaymentIssueLoading
            }
            title={i18n.t('entity.checkout.actions.issuePayment')}
            data-testid={suffixTestId('issuePayment', props)}
          />
        </ButtonGroup>
      </HStack>
    </VStack>
  );
}
